import * as React from 'react';

import { faArrowDown, faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import {
    DashboardMetricBadges,
    DashboardMetricPeriodIndicator,
} from 'src/features/dashboard/components/DashboardMetric';
import { dashboardMessages } from 'src/features/dashboard/utils/messages';
import { OCCUPANCY_REPORT_TYPE, type OccupancyStatistic, type SpacesStatistic } from 'src/features/statistics/types';
import { TOTAL_SPACES_NAME } from 'src/features/statistics/utils';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockTitle,
    GeneralBlockValue,
} from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockHeader,
    SectionBlockHead,
    SectionBlockTitle,
    SectionBlockSeparator,
    SectionBlockBody,
} from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Skeleton } from 'src/registry/ui/Skeleton';

interface DashboardWidgetMetricsProps {
    spacesStatistics?: SpacesStatistic[];
    isLoadingSpacesStatistics: boolean;
    errorUpdateCountSpacesStatistics: number;
    occupancyStatistics?: OccupancyStatistic[];
    isLoadingOccupancyStatistics: boolean;
    errorUpdateCountOccupancyStatistics: number;
}
function DashboardWidgetMetrics({
    spacesStatistics,
    isLoadingSpacesStatistics,
    errorUpdateCountSpacesStatistics,
    occupancyStatistics,
    isLoadingOccupancyStatistics,
    errorUpdateCountOccupancyStatistics,
}: DashboardWidgetMetricsProps) {
    const { formatMessage } = useIntl();

    const [totalSpacesStatistic, occupiedPercentage] = React.useMemo(() => {
        if (!spacesStatistics) return [];

        // The SmartPark API returns a psuedo-zone with the collective totalSpacesStatistic of all zones. We simply want to filter it here.
        const totalSpaces = spacesStatistics.find((zone) => zone.parking_id === TOTAL_SPACES_NAME);

        const total_spaces_count = totalSpaces?.total_spaces_count || 1;

        const occupiedPercentage = totalSpaces ? (totalSpaces?.onfield_spaces_busy / total_spaces_count) * 100 : 0;

        return [totalSpaces, occupiedPercentage];
    }, [spacesStatistics]);

    const [occupancyStatisticsToday, occupancyStatisticsWeek, occupancyYesterdayDiffPercentage] = React.useMemo(() => {
        if (!occupancyStatistics || (Array.isArray(occupancyStatistics) && occupancyStatistics.length === 0)) return [];

        const todayDate = new Date();
        const yesterdayDate = new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate() - 1);
        todayDate.setHours(0, 0, 0, 0);
        yesterdayDate.setHours(0, 0, 0, 0);

        const today =
            occupancyStatistics.find(
                (stat) => new Date(stat.period_from).toDateString() === todayDate.toDateString(),
            ) ?? occupancyStatistics[0];
        const yesterday =
            occupancyStatistics.find((stat) => stat.period_from === yesterdayDate.toISOString()) ??
            occupancyStatistics[1];
        const week = occupancyStatistics.find((stat) => stat.report_type === OCCUPANCY_REPORT_TYPE.TOTAL);

        const yesterday_count_total = yesterday?.count_total || 1;

        const percentageDiff = ((today.count_total - yesterday_count_total) / yesterday_count_total) * 100;

        return [today, week, percentageDiff];
    }, [occupancyStatistics]);

    return (
        <SectionBlock className="flex-1 shrink-0 sm:min-w-96">
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(dashboardMessages.metrics)}</SectionBlockTitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <SectionBlockBody className="grid h-full auto-rows-fr gap-4 sm:grid-cols-2">
                {isLoadingOccupancyStatistics && !errorUpdateCountOccupancyStatistics ? (
                    <>
                        <Skeleton />
                        <Skeleton />
                    </>
                ) : (
                    <>
                        <GeneralBlock>
                            <GeneralBlockHeader variant="dashboard">
                                <GeneralBlockTitle>{formatMessage(dashboardMessages.totalVisits)}</GeneralBlockTitle>
                                <DashboardMetricPeriodIndicator>
                                    {formatMessage(dashboardMessages.twentyFourHours)}
                                </DashboardMetricPeriodIndicator>
                            </GeneralBlockHeader>
                            <GeneralBlockBody variant="dashboard">
                                <GeneralBlockValue>{occupancyStatisticsToday?.count_total}</GeneralBlockValue>
                                <DashboardMetricBadges>
                                    {occupancyYesterdayDiffPercentage != null && (
                                        <Badge
                                            theme={
                                                occupancyYesterdayDiffPercentage > 0
                                                    ? 'emerald'
                                                    : occupancyYesterdayDiffPercentage == 0
                                                      ? 'gray'
                                                      : 'red'
                                            }
                                        >
                                            {Math.round(occupancyYesterdayDiffPercentage)}%{' '}
                                            {occupancyYesterdayDiffPercentage > 0 ? (
                                                <FontAwesomeIcon icon={faArrowUp} />
                                            ) : (
                                                <FontAwesomeIcon icon={faArrowDown} />
                                            )}
                                        </Badge>
                                    )}
                                </DashboardMetricBadges>
                            </GeneralBlockBody>
                        </GeneralBlock>

                        <GeneralBlock>
                            <GeneralBlockHeader variant="dashboard">
                                <GeneralBlockTitle>{formatMessage(dashboardMessages.totalVisits)}</GeneralBlockTitle>
                                <DashboardMetricPeriodIndicator>
                                    {formatMessage(dashboardMessages.sevenDays)}
                                </DashboardMetricPeriodIndicator>
                            </GeneralBlockHeader>
                            <GeneralBlockBody variant="dashboard">
                                <GeneralBlockValue>{occupancyStatisticsWeek?.count_total}</GeneralBlockValue>
                                <DashboardMetricBadges></DashboardMetricBadges>
                            </GeneralBlockBody>
                        </GeneralBlock>
                    </>
                )}

                {isLoadingSpacesStatistics && !errorUpdateCountSpacesStatistics ? (
                    <>
                        <Skeleton />
                        <Skeleton />
                    </>
                ) : (
                    <>
                        <GeneralBlock>
                            <GeneralBlockHeader variant="dashboard">
                                <GeneralBlockTitle>{formatMessage(dashboardMessages.occupiedSpaces)}</GeneralBlockTitle>
                                <DashboardMetricPeriodIndicator>
                                    {formatMessage(dashboardMessages.twentyFourHours)}
                                </DashboardMetricPeriodIndicator>
                            </GeneralBlockHeader>
                            <GeneralBlockBody variant="dashboard">
                                <GeneralBlockValue>{totalSpacesStatistic?.onfield_spaces_busy}</GeneralBlockValue>
                                <DashboardMetricBadges>
                                    {occupiedPercentage && (
                                        <Badge theme="gray">
                                            {Math.round(occupiedPercentage)}% {formatMessage(dashboardMessages.ofAll)}
                                        </Badge>
                                    )}
                                </DashboardMetricBadges>
                            </GeneralBlockBody>
                        </GeneralBlock>

                        <GeneralBlock>
                            <GeneralBlockHeader variant="dashboard">
                                <GeneralBlockTitle>{formatMessage(dashboardMessages.totalSpaces)}</GeneralBlockTitle>
                                <DashboardMetricPeriodIndicator>
                                    {formatMessage(dashboardMessages.twentyFourHours)}
                                </DashboardMetricPeriodIndicator>
                            </GeneralBlockHeader>
                            <GeneralBlockBody variant="dashboard">
                                <GeneralBlockValue>{totalSpacesStatistic?.total_spaces_count}</GeneralBlockValue>
                                {totalSpacesStatistic && (
                                    <DashboardMetricBadges>
                                        <Badge theme="emerald">
                                            {totalSpacesStatistic?.onfield_spaces_free}{' '}
                                            {formatMessage(dashboardMessages.availableSpacesLabel)}
                                        </Badge>
                                        <Badge theme="red">
                                            {totalSpacesStatistic?.onfield_spaces_busy}{' '}
                                            {formatMessage(dashboardMessages.occupiedSpacesLabel)}
                                        </Badge>
                                    </DashboardMetricBadges>
                                )}
                            </GeneralBlockBody>
                        </GeneralBlock>
                    </>
                )}
            </SectionBlockBody>
        </SectionBlock>
    );
}

export default DashboardWidgetMetrics;
